import { CommonModule } from '@angular/common'
import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormControl, FormGroup } from '@angular/forms'
import { PersonaTypes } from '@app/modules/personas/enums/persona-types.enum'
import { ServerRepresentedEvent } from '../../../events/models/event.model'
import { Persona } from '../../../personas/interfaces/persona.interface'
import { BadgeEventsListComponent } from './components/badge-events-list/badge-events-list.component'
import { BadgePersonaListComponent } from './components/badge-persona-list/badge-persona-list.component'
import { NameBadgeComponent } from './components/name-badge/name-badge.component'
import { BadgeEditorStep } from './enums/badge-editor-step.enum'
import { BadgeElementFieldType, TestBadgeData } from './models/badge-variable-elements.model'
import { BadgeEditorService } from './services/badge-editor.service'
import { FormTab, VariableElementResponse } from './types/badge.type'
interface EventWithForms extends Partial<ServerRepresentedEvent> {
  detailedForms?: Persona[]
}

type RegistrationForm = {
  id: number
  parentName?: string
  name: string
  acronym: string
  badge_front_html_content: string
  steps: FormTab[]
}

@Component({
  selector: 'ngx-badge-editor',
  templateUrl: './badge-editor.component.html',
  styleUrls: ['./badge-editor.component.scss'],
  standalone: true,
  imports: [CommonModule, BadgeEventsListComponent, BadgePersonaListComponent, NameBadgeComponent]
})
export class BadgeEditorComponent implements OnInit {
  @Input() embedded = false
  @Input() initialEvent: EventWithForms | null = null
  @Output() exit = new EventEmitter<void>()

  public selectedEvent?: Partial<ServerRepresentedEvent>
  public selectedPersona?: Partial<Persona>
  public badgePersona?: RegistrationForm
  public personas: Partial<Persona>[] = []
  public currentStep: BadgeEditorStep = BadgeEditorStep.EVENT_SELECTION
  public badgeEditorStep = BadgeEditorStep
  public badgeForm: FormGroup = new FormGroup({})
  public variableElements: VariableElementResponse[] = []
  public parentVariableElements: VariableElementResponse[] = []
  private readonly _destroyRef = inject(DestroyRef)

  constructor(private badgeEditorService: BadgeEditorService) {}

  ngOnInit(): void {
    if (this.embedded && this.initialEvent) {
      this.onEventSelected(this.initialEvent)
    }
  }

  public onEventSelected(event: EventWithForms): void {
    this.selectedEvent = event
    // Use detailed forms if available, otherwise fallback to basic forms
    this.personas = this._removePersonasWithGroupForm((event.detailedForms || event.forms || []) as Persona[])
    this.currentStep = BadgeEditorStep.PERSONA_SELECTION
  }

  public onBack(step: BadgeEditorStep): void {
    this.currentStep = step
    this.selectedPersona = undefined
    this.badgePersona = undefined
    this.variableElements = []
    this.parentVariableElements = []
    this.badgeForm = new FormGroup({})
    if (step === BadgeEditorStep.EVENT_SELECTION) {
      this.selectedEvent = undefined
      this.personas = []
      if (this.embedded) {
        this.exit.emit()
      }
    }
  }

  public onCancelTemplateSelection(): void {
    // Handle template selection cancellation
    this.currentStep = BadgeEditorStep.PERSONA_SELECTION
  }

  /**
   * @description Handle persona selection
   * @param persona Persona
   */
  public onPersonaSelected(persona: Persona): void {
    this.selectedPersona = persona
    const parentPersona = persona.parent ? this.selectedEvent?.forms?.find((p) => p.id === persona.parent) : undefined
    this.badgeEditorService.isDTCM = !!(persona?.options?.dtcm?.section || persona?.options?.dtcm?.eventCode)
    this._setSteps(persona, parentPersona as Persona)
    this._setVariableElements(persona)
    if (parentPersona) {
      this._setParentVariableElements(parentPersona as Persona)
    }
    this.fetchBadge(persona.id as number)
  }

  /**
   * @description Set the steps for the persona
   * @param persona Persona
   * @param parentPersona Persona
   */
  private _setSteps(persona: Persona, parentPersona?: Persona): void {
    const fields = [
      ...persona.form_elements.map((field) => ({
        id: field.id.toString(),
        name: field.name,
        field_type: field.name,
        label: field.label
      })),
      ...(parentPersona
        ? parentPersona.form_elements.map((field) => ({
            id: field.id.toString(),
            name: field.name,
            field_type: `parent_${field.name}`,
            label: field.label
          }))
        : [])
    ]
    this.badgePersona = {
      id: persona.id,
      parentName: parentPersona?.name,
      name: persona.type === PersonaTypes.GENERIC ? 'category' : persona.name,
      acronym: persona.slug,
      badge_front_html_content: persona.badge_front_html_content as string,
      steps: [
        {
          name: '',
          fields: [fields]
        }
      ]
    }
    this.badgeEditorService.personaFields =
      this.badgePersona.steps?.reduce((acc: any[], step: FormTab) => {
        return acc.concat(step.fields || [])
      }, []) || []
  }

  /**
   * @description Set the child variable elements for the persona
   * @param persona Persona
   */
  private _setVariableElements(persona: Persona): void {
    this.variableElements = [
      ...persona.form_elements.map((field) => ({
        field_type: field.name,
        label: field.label,
        value: TestBadgeData[field.name],
        weight: field.weight
      }))
    ]
    this.variableElements.push({
      field_type: BadgeElementFieldType.PERSONA_NAME.toLowerCase(),
      label: 'Persona name'
    })
    this.variableElements.push({
      field_type: BadgeElementFieldType.SPACER.toLowerCase(),
      label: 'Spacer'
    })
    this.variableElements.push({
      field_type: BadgeElementFieldType.QR_CODE.toLowerCase(),
      label: 'QR-code'
    })
    this.variableElements.push({
      field_type: BadgeElementFieldType.DTCM_QR_CODE.toLowerCase(),
      label: 'DTCM QR-code'
    })
  }

  /**
   * @description Set the parent variable elements for the persona
   * @param persona Persona
   */
  private _setParentVariableElements(persona: Persona): void {
    this.parentVariableElements = [
      ...persona.form_elements.map((field) => ({
        field_type: `parent_${field.name}`,
        label: 'Parent: ' + field.label,
        value: 'Parent: ' + TestBadgeData?.[field.name],
        weight: field.weight
      })),
      {
        field_type: `parent_${BadgeElementFieldType.PERSONA_NAME.toLowerCase()}`,
        label: 'Parent: Persona name'
      }
    ]
  }

  /**
   * @description Fetch the badge for the persona
   * @param form_id number
   */
  public fetchBadge(form_id: number): void {
    this.badgeEditorService
      .getBadge(this.selectedPersona?.id as number)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((res) => {
        this.currentStep = BadgeEditorStep.BADGE_PREVIEW
        this.badgeForm = new FormGroup({
          id: new FormControl(form_id),
          badge_front_html_content: new FormControl(res.badge?.badge_front_html_content),
          background_preprinted: new FormControl(res.badge?.background_preprinted),
          front_fixed_element: new FormControl(res.badge?.front_fixed_element)
        })
        this.badgeEditorService.frontFixedElement = res.badge?.front_fixed_element
      })
  }

  /**
   * @description Remove personas with group form from the list with type 30, this number means the persona has a group form
   * @param personas Persona[]
   * @returns Persona[]
   */
  private _removePersonasWithGroupForm(personas: Persona[]): Persona[] {
    return personas.filter((persona) => persona?.type !== 30)
  }
}
